import { useLazyLoadQuery } from 'react-relay';
import { PDP_DESKTOP_AGGREGATION_FEB_2025 } from '../../../constants/abTestsConstants';
import { type FC, type ComponentProps, useEffect } from 'react';
import {
    pdpRespMarketplacePrimaryRootQuery,
    type PdpRespMarketplacePrimaryRootQueryType,
    type pdpRespMarketplacePrimaryRootQuery$variables as PrimaryRelayVariables,
} from '../../../queries/pdpRespMarketplacePrimaryRoot';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';
import { DblExternalContextProvider } from 'dibs-buyer-layout/exports/DblExternalContext';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';

// components
import { Provider } from 'react-redux';
import { IntlProvider } from 'dibs-react-intl';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary';
import { Error500Layout } from 'dibs-buyer-layout/exports/error500';
import { PdpRespMarketplaceLayoutPrimary } from './PdpRespMarketplaceLayoutPrimary';
import { PdpRespMarketplaceLayoutSecondaryRenderer } from './PdpRespMarketplaceLayoutSecondaryRenderer';

// types
import { type Store } from 'redux';
import { type Locale } from 'dibs-intl/exports/locales';
import { type pdpRespMarketplaceSecondaryRootQuery$variables as SecondaryRelayVariables } from '../../../queries/pdpRespMarketplaceSecondaryRoot';

type IntlProviderProps = ComponentProps<typeof IntlProvider>;
type Messages = IntlProviderProps['messages'];

export const PdpRespMarketplaceLayout: FC<{
    store: Store;
    locale: Locale;
    messages: Messages;
    primaryRelayVariables: PrimaryRelayVariables;
    secondaryRelayVariables: SecondaryRelayVariables;
}> = ({ locale, messages, primaryRelayVariables, secondaryRelayVariables, store }) => {
    const primaryData = useLazyLoadQuery<PdpRespMarketplacePrimaryRootQueryType>(
        pdpRespMarketplacePrimaryRootQuery,
        primaryRelayVariables,
        { fetchPolicy: 'store-only' }
    );

    useDwelledOnPage(PAGE_TYPE.PDP);

    // test tracking
    useEffect(() => {
        trackAbTestV2Variant(PDP_DESKTOP_AGGREGATION_FEB_2025);
    }, []);

    return (
        <IntlProvider locale={locale} messages={messages}>
            <ErrorBoundary fallback={({ error }) => <Error500Layout error={error} />}>
                <Provider store={store}>
                    <PdpRespMarketplaceLayoutPrimary viewer={primaryData.viewer} />
                </Provider>
                <DblExternalContextProvider store={store}>
                    <PdpRespMarketplaceLayoutSecondaryRenderer
                        relayVariables={secondaryRelayVariables}
                        ssr={!!primaryRelayVariables.afterDisplayOrBuaSsr}
                    />
                </DblExternalContextProvider>
            </ErrorBoundary>
        </IntlProvider>
    );
};
